/* eslint-disable no-unused-vars */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Textarea from '@components/textarea';
import TextBoxUnit from '@components/text-box-unit';
import InputField from '@components/input-field';
import Portal from '@components/portal';
import Icon from '@components/icon-component';
import Button from '@components/button';
import Select from '@components/select-box-component';
import SaveModal from '@components/modal/variants/save-modal';
import { v4 as uuidv4 } from 'uuid';
import Label from '@components/label';

import {
  getFormFieldsOfCML,
  getFormFieldsOfReading,
  defaultValuesOfCML,
  addEditCMLSchema,
  getDefaultValue,
  addEditReadingSchema,
  setDefaultValuesOnForm,
  convertValue,
  checkValueIsEmpty
} from './helper';

import style from './style.module.scss';
import SectionContainer from '@components/section-container';
import DeleteModal from '@components/modal/variants/delete-modal';
import { useBlocker } from 'react-router-dom';
import {
  initialConversionWhenDataIsLoaded,
  setTminValueWhenCOmponentValueIsChanged,
  setValueWhenUnitIsChanged
} from './conversion';

const AddEditCMLModal = ({
  open,
  title = 'Add PSV',
  closed,
  dropdownMenuMapList,
  isCML,
  isReading,
  cachedData,
  listOfCMLNumber,
  addNewCMLDataEvent,
  updateCMLDataEvent,
  deleteSelectedCML,
  shellTminAndHeadTminValue,
  picklistsMap,
  persistUnit
}) => {
  const [isDeletedModalOpen, setIsDeletedModalOpen] = useState(false);
  const [saveChanges, setSaveChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disabledBtn, setDisabledBtn] = useState(false);
  const [showTminBox, setShowTminBox] = useState(false);
  const [tminBoxValue, setTminBoxValue] = useState(false);
  const [disabledSkipButton, setDisabledSkipButton] = useState(false);
  const [openAddReadingForm, setOpenAddReadingForm] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(persistUnit || 'in');

  const isReadingAvailable = isReading || openAddReadingForm;
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields, touchedFields },
    setValue,
    getValues,
    watch,
    reset,
    register,
    trigger
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      yup.object().shape({
        ...addEditCMLSchema,
        ...(isReadingAvailable ? addEditReadingSchema : {})
      })
    ),
    defaultValues: getDefaultValue({ isCML, isReadingAvailable })
  });

  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      ((isDirty && Object.keys(dirtyFields)?.length) || open) &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (isDirty && Object.keys(dirtyFields)?.length) setSaveChanges(true);
      else resetAllData();
    }
  }, [blocker, dirtyFields]);

  useEffect(() => {
    if (cachedData) {
      setLoading(true);
      const {
        openTminTextBox,
        tminBoxValue: tminBoxValueConversion,
        tminFormValue,
        fieldName
      } = initialConversionWhenDataIsLoaded({
        cachedData,
        picklistsMap,
        shellTminAndHeadTminValue,
        selectedUnit,
        persistUnit
      });

      setShowTminBox(openTminTextBox);
      setTminBoxValue(tminBoxValueConversion);
      setValue(`${fieldName}.textBoxValue`, tminFormValue, {
        shouldDirty: true
      });

      const reconcileDataAndDefaultValues = setDefaultValuesOnForm({ cachedData });
      reset({
        ...reconcileDataAndDefaultValues
      });
      setSelectedUnit(persistUnit || 'in');
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [cachedData, shellTminAndHeadTminValue, picklistsMap]);

  useEffect(() => {
    if (!loading && persistUnit) {
      selectedUnitEvent({ unit: persistUnit, initialRender: true });
    }
  }, [persistUnit, loading]);

  const formFieldsOfCML = useMemo(() => {
    return getFormFieldsOfCML({
      dropdownMenuMapList,
      isReconcile: cachedData?.isCached || false,
      selectedUnit
    });
  }, [dropdownMenuMapList, cachedData, selectedUnit]);

  const formFieldsOfReading = useMemo(() => {
    return getFormFieldsOfReading({ dropdownMenuMapList });
  }, [dropdownMenuMapList]);

  const goBack = () => {
    if (isDirty && Object.keys(dirtyFields)?.length) setSaveChanges(true);
    else {
      resetAllData();
    }
  };

  const isSaveButtonDisabled = useMemo(() => {
    return !isDirty && !Object.keys(dirtyFields)?.length ? true : false;
  }, [isDirty, dirtyFields]);

  useEffect(() => {
    removeUnitAndSetUnitIfDataIsEnteredOnField();
  }, [watch(), dropdownMenuMapList, selectedUnit, cachedData]);

  const removeUnitAndSetUnitIfDataIsEnteredOnField = async () => {
    let list =
      selectedUnit === 'mm'
        ? [
            'outsideDiameterOnMM',
            'nominalDiameterOnMM',
            'wallThicknessOnMM',
            'tminOnMM',
            'minReading',
            'avgReading'
          ]
        : [
            'outsideDiameter',
            'nominalDiameter',
            'wallThickness',
            'tmin',
            'minReading',
            'avgReading'
          ];
    let inchList = dropdownMenuMapList?.SmallMeasurementPickListForCML?.reduce((acc, curr) => {
      acc[curr.label] = curr.value;
      return acc;
    }, {});
    const selectedUnitName = inchList[selectedUnit];

    for (let field of list) {
      let val = watch(`${field}.textBoxValue`);

      const checkIsNullUndefinedOrEmpty = checkValueIsEmpty({ value: val });

      if (checkIsNullUndefinedOrEmpty && !watch(`${field}.textBoxUnitValue`)) {
        setValue(`${field}.textBoxUnitValue`, selectedUnitName);
      } else if (!checkIsNullUndefinedOrEmpty && watch(`${field}.textBoxUnitValue`)) {
        setValue(`${field}.textBoxUnitValue`, '');
        await trigger(field);

        const noConcat = ['minReading', 'avgReading'].includes(field);
        const previousFieldName =
          selectedUnit === 'in'
            ? noConcat
              ? field
              : field?.concat('OnMM')
            : selectedUnit === 'mm'
            ? noConcat
              ? field
              : field.replace('OnMM', '')
            : '';

        let valSec = watch(`${previousFieldName}.textBoxValue`);
        const checkIsNullUndefinedOrEmptyOtherValue = checkValueIsEmpty({ value: valSec });
        if (checkIsNullUndefinedOrEmptyOtherValue) {
          setValue(`${previousFieldName}.textBoxUnitValue`, '');
          setValue(`${previousFieldName}.textBoxValue`, '');
        }
      }
    }

    let minReadingTextBoxValue = watch(`minReading.textBoxValue`);
    let condMinReadingTextBoxValue = checkValueIsEmpty({ value: minReadingTextBoxValue });

    let avgReadingTextBoxValue = watch(`avgReading.textBoxValue`);
    let condAvgReadingTextBoxValue = checkValueIsEmpty({ value: avgReadingTextBoxValue });

    if (
      condMinReadingTextBoxValue &&
      watch(`minReading.textBoxUnitValue`) &&
      condAvgReadingTextBoxValue &&
      watch(`avgReading.textBoxUnitValue`)
    ) {
      setDisabledSkipButton(true);
    } else {
      setDisabledSkipButton(false);
    }
  };

  let changeCOmponentValueEvent = ({ componentValue }) => {
    if (componentValue) {
      const {
        tminBoxValue: tminBoxValueConversion,
        tminFormValue,
        fieldName,
        changeDecimalValueBasedOnUnit: _changeDecimalValueBasedOnUnit,
        openTminTextBox
      } = setTminValueWhenCOmponentValueIsChanged({
        picklistsMap,
        componentValue,
        shellTminAndHeadTminValue,
        selectedUnit,
        cachedData
      });

      setShowTminBox(openTminTextBox);
      setTminBoxValue(tminBoxValueConversion);
      setValue(`${fieldName}.textBoxValue`, tminFormValue, {
        shouldDirty: true
      });

      if (_changeDecimalValueBasedOnUnit) {
        setTimeout(() => {
          changeDecimalValueBasedOnUnit({
            name: fieldName,
            latestUnit: selectedUnit
          });
        }, 100);
      }
    }
  };

  const isCMLNumberALreadyExistsInTheList = useMemo(() => {
    let exists = false;
    if (watch('cmlNumber') && listOfCMLNumber?.length > 0) {
      exists = listOfCMLNumber.some(
        (x) => x?.cmlNumber === watch('cmlNumber') && cachedData.id !== x.id
      );
    }
    return exists;
  }, [watch('cmlNumber'), listOfCMLNumber, cachedData]);

  const onSubmitHandler = async (data) => {
    let inchList = dropdownMenuMapList?.SmallMeasurementPickListForCML?.reduce((acc, curr) => {
      acc[curr.label] = curr.value;
      return acc;
    }, {});

    if (isCMLNumberALreadyExistsInTheList) {
      return;
    } else {
      if (!cachedData) {
        setDisabledBtn(true);
        const res = await addNewCMLDataEvent({
          data: {
            ...data,
            selectedLengthUnit: selectedUnit,
            id: uuidv4()
          },
          isDone: disabledSkipButton ? true : false,
          isSkipped: false,
          isAddReading: isReadingAvailable ? false : true
        });
        resetAllData();
      } else {
        // Updated
        setDisabledBtn(true);
        const newObjData = { ...data };
        //  Object.fromEntries(
        //   Object.entries(data).filter(([key]) => !key.includes('-reconcile'))
        // );

        let submitData = { ...newObjData, selectedLengthUnit: selectedUnit };
        if (
          selectedUnit === 'mm'
          // && cachedData?.isCached
        ) {
          let list = [
            'outsideDiameterOnMM',
            'nominalDiameterOnMM',
            'wallThicknessOnMM',
            'tminOnMM'
          ];

          for (let field of list) {
            let unitValue = watch(`${field}.textBoxUnitValue`) || null;
            let textValue = watch(`${field}.textBoxValue`);

            const checkIsNullUndefinedOrEmpty = checkValueIsEmpty({ value: textValue });

            if (unitValue && checkIsNullUndefinedOrEmpty) {
              let newConvertValue = convertValue({
                value: textValue,
                convertedUnit: 'in',
                mmToFixedValue: field === 'tminOnMM' ? 6 : 5,
                inToFixedValue: field === 'tminOnMM' ? 8 : 7
              });
              const selectedUnitName = inchList['in'];

              let newField = field.replace('OnMM', '');
              submitData = {
                ...submitData,
                [newField]: {
                  textBoxValue: newConvertValue,
                  textBoxUnitValue: selectedUnitName
                }
              };
            }
          }
        }

        const res = await updateCMLDataEvent({
          data: submitData
        });
        resetAllData();
      }
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmitHandler)(event);
  };

  const onKeyDown = ({ e, isTmin = false }) => {
    // const decimalPlaces = selectedUnit === 'in' ? 7 : isTmin ? 6 : 5;
    const decimalPlaces = selectedUnit === 'in' ? 7 : isTmin ? 6 : 5;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End'
    ];

    if (allowedKeys.includes(e.key)) return;

    const value = e.target.value;
    const decimalIndex = value.indexOf('.');

    if (decimalIndex > -1) {
      const decimals = value.substring(decimalIndex + 1);

      if (decimals.length >= decimalPlaces) {
        // Block only number input
        if (/^[0-9]$/.test(e.key)) {
          e.preventDefault();
        }
      }
    }
  };

  const deleteEvent = () => {
    setIsDeletedModalOpen(true);
  };

  const handleSave = () => {
    handleSubmit((data) => onSubmitHandler(data))();
    setSaveChanges((prev) => !prev);
  };

  const isInputReconcile = useCallback(
    ({ fieldName, isMasterData }) => {
      return !_.isEmpty(watch(`${fieldName}-reconcile`))
        ? isMasterData && watch(`${fieldName}-reconcile`) !== watch(fieldName)
          ? true
          : false
        : !_.isEmpty(watch(fieldName)) && isMasterData;
    },
    [watch]
  );

  const isSelectBoxReconcile = useCallback(
    ({ fieldName, isMasterData, options }) => {
      return !_.isEmpty(watch(`${fieldName}-reconcile`))
        ? isMasterData &&
          options?.length > 0 &&
          watch(`${fieldName}-reconcile`) !== watch(fieldName)
          ? true
          : false
        : !_.isEmpty(watch(fieldName)) && options?.length > 0 && isMasterData;
    },
    [watch]
  );

  const isTextBoxUnitReconcile = useCallback(
    ({ fieldName, isMasterData }) => {
      return !_.isEmpty(watch(`${fieldName}-reconcile.textBoxUnitValue`))
        ? !_.isNull(watch(`${fieldName}-reconcile.textBoxValue`)) &&
          isMasterData &&
          watch(`${fieldName}.textBoxValue`)?.toString() !==
            watch(`${fieldName}-reconcile.textBoxValue`)?.toString()
          ? true
          : watch(`${fieldName}.textBoxUnitValue`) !==
            watch(`${fieldName}-reconcile.textBoxUnitValue`)
          ? true
          : false
        : !_.isNull(watch(`${fieldName}.textBoxValue`)) &&
            !_.isEmpty(watch(`${fieldName}.textBoxUnitValue`)) &&
            isMasterData;
    },
    [watch]
  );

  const isNewForm = !cachedData;

  const clickAddNewCMLEvent = () => {
    setOpenAddReadingForm(true);
  };

  const selectedUnitEvent = ({ unit, initialRender = false }) => {
    let inchList = dropdownMenuMapList?.SmallMeasurementPickListForCML?.reduce((acc, curr) => {
      acc[curr.label] = curr.value;
      return acc;
    }, {});

    const selectedUnitName = inchList[unit];

    let list =
      unit === 'mm'
        ? [
            'outsideDiameterOnMM',
            'nominalDiameterOnMM',
            'wallThicknessOnMM',
            'tminOnMM',
            'minReading',
            'avgReading'
          ]
        : [
            'outsideDiameter',
            'nominalDiameter',
            'wallThickness',
            'tmin',
            'minReading',
            'avgReading'
          ];

    for (let field of list) {
      const isTmin = field === 'tmin' || field === 'tminOnMM' ? true : false;
      let unitValue = watch(`${field}.textBoxUnitValue`) || null;
      let textValue = watch(`${field}.textBoxValue`);

      const checkIsNullUndefinedOrEmpty = checkValueIsEmpty({ value: textValue });

      const noConcat = ['minReading', 'avgReading'].includes(field);
      // previous textbox unit have some values
      if (unitValue && checkIsNullUndefinedOrEmpty) {
        onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveSomeOldValue({
          latestSelectedUnitName: selectedUnitName,
          previousUnitValue: unitValue,
          previousTextValue: textValue,
          latestSelectedUnit: unit,
          isTmin,
          field,
          noConcat,
          initialRender
        });
      } else {
        onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveNoValue({
          latestSelectedUnitName: selectedUnitName,
          latestSelectedUnit: unit,
          isTmin,
          field,
          noConcat,
          initialRender
        });
      }
    }

    const componentValue = watch('component') || '';
    if (componentValue) {
      const {
        tminBoxValue: tminBoxValueConversion,
        tminFormValue,
        fieldName,
        openTminTextBox
      } = setValueWhenUnitIsChanged({
        picklistsMap,
        selectedUnit: unit,
        componentValue,
        shellTminAndHeadTminValue
      });

      setShowTminBox(openTminTextBox);
      setTminBoxValue(tminBoxValueConversion);
      setValue(`${fieldName}.textBoxValue`, tminFormValue, {
        shouldDirty: true
      });
    }
    setSelectedUnit(unit);
  };

  const onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveSomeOldValue = ({
    latestSelectedUnitName, //selectedUnitName
    previousUnitValue, //unitValue
    previousTextValue, // textValue
    latestSelectedUnit, // unit
    isTmin,
    isSaveButtonDisabled,
    field,
    noConcat,
    initialRender
  }) => {
    function ifSelectedUnitAndPreviousUnitAreNotSame() {
      let newConvertValue = convertValue({
        value: previousTextValue,
        convertedUnit: latestSelectedUnit,
        mmToFixedValue: isTmin ? 6 : 5,
        inToFixedValue: isTmin ? 8 : 7
      });

      setValue(`${field}.textBoxUnitValue`, latestSelectedUnitName, { shouldDirty: true });
      setValue(`${field}.textBoxValue`, newConvertValue, { shouldDirty: true });

      setTimeout(() => {
        changeDecimalValueBasedOnUnit({ name: field, latestUnit: latestSelectedUnit });
      }, 100);
    }

    function ifSelectedUnitAndPreviousUnitAreSameButSaveButtonAreDisabled() {
      let newConvertValue = previousTextValue;
      setValue(`${field}.textBoxValue`, newConvertValue, { shouldDirty: true });
      setValue(`${field}.textBoxUnitValue`, latestSelectedUnitName, { shouldDirty: true });
    }

    function ifSelectedUnitAndPreviousUnitAreSameButSaveButtonAreNotDisabled() {
      const previousFieldName =
        latestSelectedUnit === 'in'
          ? noConcat
            ? field
            : field?.concat('OnMM')
          : latestSelectedUnit === 'mm'
          ? noConcat
            ? field
            : field.replace('OnMM', '')
          : '';

      let previousTextValueOfOtherField = watch(`${previousFieldName}.textBoxValue`);

      const checkINotsNullUndefinedOrEmpty = checkValueIsEmpty({
        value: previousTextValueOfOtherField
      });
      if (checkINotsNullUndefinedOrEmpty) {
        let newConvertValueBasedOnOldValue = convertValue({
          value: previousTextValueOfOtherField,
          convertedUnit: latestSelectedUnit,
          mmToFixedValue: isTmin ? 6 : 5,
          inToFixedValue: isTmin ? 8 : 7
        });

        setTimeout(() => {
          changeDecimalValueBasedOnUnit({ name: field, latestUnit: latestSelectedUnit });
        }, 100);

        setValue(`${field}.textBoxValue`, newConvertValueBasedOnOldValue, {
          shouldDirty: true
        });
        setValue(`${field}.textBoxUnitValue`, latestSelectedUnitName, { shouldDirty: true });
      } else {
        setValue(`${field}.textBoxValue`, '', {
          shouldDirty: true
        });
        setValue(`${field}.textBoxUnitValue`, '', { shouldDirty: true });
      }
    }

    function ifSelectedUnitAndPreviousUnitAreSame() {
      if (isSaveButtonDisabled || initialRender) {
        ifSelectedUnitAndPreviousUnitAreSameButSaveButtonAreDisabled();
      } else {
        ifSelectedUnitAndPreviousUnitAreSameButSaveButtonAreNotDisabled();
      }
    }

    // case 1 if  previous and current are not same
    if (latestSelectedUnitName !== previousUnitValue) {
      ifSelectedUnitAndPreviousUnitAreNotSame();
    } else if (latestSelectedUnitName === previousUnitValue) {
      ifSelectedUnitAndPreviousUnitAreSame();
    }
  };

  const onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveNoValue = ({
    latestSelectedUnitName, //selectedUnitName
    latestSelectedUnit, // unit
    isTmin,
    field,
    noConcat,
    initialRender
  }) => {
    function onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveNoValueAndSelectedUnitIsMM() {
      let previousTextBoxValue = watch(
        `${noConcat ? field : field.replace('OnMM', '')}.textBoxValue`
      );
      let previousTextBoxUnitValue = watch(
        `${noConcat ? field : field.replace('OnMM', '')}.textBoxUnitValue`
      );

      let checkIsNotNullUndefinedOrEmpty = checkValueIsEmpty({ value: previousTextBoxValue });

      if (checkIsNotNullUndefinedOrEmpty && latestSelectedUnitName !== previousTextBoxUnitValue) {
        let newConvertValue = convertValue({
          value: previousTextBoxValue,
          convertedUnit: latestSelectedUnit,
          mmToFixedValue: isTmin ? 6 : 5,
          inToFixedValue: isTmin ? 8 : 7
        });

        setValue(`${field}.textBoxUnitValue`, latestSelectedUnitName, { shouldDirty: true });
        setValue(`${field}.textBoxValue`, newConvertValue, { shouldDirty: true });

        setTimeout(() => {
          changeDecimalValueBasedOnUnit({ name: field, latestUnit: latestSelectedUnit });
        }, 100);
      }
    }

    function onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveNoValueAndSelectedUnitIsIN() {
      let previousTextBoxValue = watch(`${noConcat ? field : field?.concat('OnMM')}.textBoxValue`);
      let previousTextBoxUnitValue = watch(
        `${noConcat ? field : field?.concat('OnMM')}.textBoxUnitValue`
      );

      let checkIsNotNullUndefinedOrEmpty = checkValueIsEmpty({ value: previousTextBoxValue });

      if (checkIsNotNullUndefinedOrEmpty && latestSelectedUnitName !== previousTextBoxUnitValue) {
        let newConvertValue = convertValue({
          value: previousTextBoxValue,
          convertedUnit: latestSelectedUnit,
          mmToFixedValue: isTmin ? 6 : 5,
          inToFixedValue: isTmin ? 8 : 7
        });

        setValue(`${field}.textBoxUnitValue`, latestSelectedUnitName, {
          shouldDirty: true
        });
        setValue(`${field}.textBoxValue`, newConvertValue, { shouldDirty: true });

        setTimeout(() => {
          changeDecimalValueBasedOnUnit({
            name: field,
            latestUnit: latestSelectedUnit,
            newConvertValue
          });
        }, 100);
      }
    }

    if (latestSelectedUnit === 'mm') {
      onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveNoValueAndSelectedUnitIsMM();
    } else if (latestSelectedUnit === 'in') {
      onChangingUnitSetNewValueIfPreviousTextBoxUnitHaveNoValueAndSelectedUnitIsIN();
    }
  };

  const resetAllData = () => {
    closed(false);
    blocker?.reset?.();
    reset({ ...defaultValuesOfCML });
    setDisabledBtn(false);
  };

  useEffect(() => {
    if (cachedData && !loading) {
      setTimeout(() => {
        let list =
          selectedUnit === 'mm'
            ? [
                'outsideDiameterOnMM',
                'nominalDiameterOnMM',
                'wallThicknessOnMM',
                'tminOnMM',
                'minReading',
                'avgReading'
              ]
            : [
                'outsideDiameter',
                'nominalDiameter',
                'wallThickness',
                'tmin',
                'minReading',
                'avgReading'
              ];

        for (let name of list) {
          changeDecimalValueBasedOnUnit({
            name
          });
        }
      }, [100]);
    }
  }, [cachedData, loading, selectedUnit]);

  const changeDecimalValueBasedOnUnit = ({ name, latestUnit = null }) => {
    let fieldName = `${name}.textBoxValue`;
    let textBox = document.getElementById(`phone-input-${fieldName}`);
    let textBoxValue = textBox?.value;
    if (textBoxValue) {
      textBoxValue = Number(textBoxValue);
      let newUnit = latestUnit ? latestUnit : selectedUnit;
      const isTmin = name === 'tminOnMM' || name === 'tmin' ? true : false;
      let newValue = textBoxValue.toFixed(newUnit === 'in' ? (isTmin ? 5 : 4) : isTmin ? 6 : 5);

      textBox.value = splitDecimalPoint({
        newValue,
        name
      });
    }
  };

  const splitDecimalPoint = ({ newValue, name }) => {
    let pointValue = ['tmin', 'tminOnMM'].includes(name) ? 4 : 3;
    let originalValue = newValue;
    originalValue = originalValue.toString();
    let [valueWithoutDecimal, decimalValue] = originalValue.split('.');

    let latestValue = newValue;
    if (decimalValue) {
      if (decimalValue.split('').length === pointValue) {
        latestValue = newValue;
      } else {
        let checkLastValueOfDecimal = decimalValue.slice(-1);

        if (checkLastValueOfDecimal === '0') {
          let valueAfterRemovingLastDigitOnDecimalPoint = decimalValue.slice(0, -1);
          checkLastValueOfDecimal = valueAfterRemovingLastDigitOnDecimalPoint.slice(-1);
          if (checkLastValueOfDecimal !== '0') {
            let bo = decimalValue.slice(0, -1);
            latestValue = `${valueWithoutDecimal}.${bo}`;
          } else if (checkLastValueOfDecimal === '0') {
            let bo = decimalValue.slice(0, pointValue);
            latestValue = bo;
            latestValue = `${valueWithoutDecimal}.${bo}`;
          }
        } else {
          latestValue = newValue;
        }
      }
    } else {
      latestValue = newValue;
    }

    return latestValue;
  };

  return (
    <Portal>
      <form id="addCML" onSubmit={onSubmit}>
        <div className={open ? style.OpenPSVsModal : style.ClosedPSVsModal}>
          {loading ? (
            <>Loading...</>
          ) : (
            <>
              {/* header */}
              <div className={` px-4 md:px-8 py-7 ${style.header} `}>
                <p className={style.cancelClass} onClick={goBack}>
                  Cancel
                </p>
                <h1 className={`h1-semibold ${style.addClass}`}>{title}</h1>
                <div className={style.invisibleDiv}>Cancel</div>
              </div>
              <div className={style.modalHeight}>
                {/* Section */}
                <>
                  <div className={style.addFormSection}>
                    <SectionContainer
                      text={'Unit selection'}
                      subSection
                      headerClassName={`${style.utSectionOfPhotos}`}
                    ></SectionContainer>
                  </div>
                  <div className={style.selectUnitForm}>
                    <div className={style.buttonContainer}>
                      <button
                        onClick={() => selectedUnit !== 'in' && selectedUnitEvent({ unit: 'in' })}
                        type="button"
                        className={selectedUnit !== 'in' && style.white}
                      >
                        <span>Inches</span>
                      </button>
                      <button
                        onClick={() => selectedUnit !== 'mm' && selectedUnitEvent({ unit: 'mm' })}
                        type="button"
                        className={selectedUnit !== 'mm' && style.white}
                      >
                        <span>millimetres</span>
                      </button>
                    </div>
                  </div>
                </>

                {isReadingAvailable && !openAddReadingForm && (
                  <>
                    <div className={style.addFormSection}>
                      <SectionContainer
                        text={'Readings'}
                        subSection
                        headerClassName={`${style.utSectionOfPhotos}`}
                      ></SectionContainer>
                    </div>

                    {/* Form */}
                    <div className={style.addDocumentForm}>
                      {formFieldsOfReading?.map((field, index) => {
                        return (
                          <div
                            className={
                              index === formFieldsOfReading?.length - 1 ? '' : style['mb-16']
                            }
                            key={field?.order}
                          >
                            {field?.type === 'Readings' && (
                              <TextBoxUnit
                                inputLabel={`${field?.label}`}
                                control={control}
                                register={register}
                                inputFiledRequired={field?.required}
                                setValue={setValue}
                                watch={watch}
                                error={
                                  watch(field?.name)?.textBoxValue &&
                                  watch(field?.name)?.textBoxUnitValue
                                    ? null
                                    : errors[field?.name]?.textBoxValue?.message ||
                                      errors[field?.name]?.textBoxUnitValue?.message
                                    ? 'Required'
                                    : null
                                }
                                textBoxUnitValueError={
                                  watch(field?.name)?.textBoxUnitValue
                                    ? null
                                    : errors[field?.name]?.textBoxUnitValue?.message
                                    ? true
                                    : null
                                }
                                textBoxValueError={
                                  watch(field?.name)?.textBoxValue
                                    ? null
                                    : errors[field?.name]?.textBoxValue?.message
                                    ? true
                                    : null
                                }
                                name={field?.name}
                                options={field?.options}
                                onKeyDown={(e) => onKeyDown({ e })}
                                disabledFieldAndHideActionBtn={true}
                                onInputBlur={() =>
                                  changeDecimalValueBasedOnUnit({ name: field?.name })
                                }
                              />
                            )}

                            {field?.type === 'Box' &&
                              (watch(field?.name) || watch(field?.name2)) && (
                                <div className={style.previousReadingContainer}>
                                  <label>{field.label}</label>
                                  <p>
                                    {selectedUnit === 'mm'
                                      ? field?.name2
                                        ? watch(field?.name2)
                                        : field?.name
                                      : watch(field?.name)}
                                  </p>
                                </div>
                              )}

                            {field?.type == 'LongText' && (
                              <Textarea
                                dot={field?.required}
                                label={field?.label}
                                inputClass="input-field input-regular"
                                labelColor={style.labelColor}
                                register={register}
                                name={field?.name}
                                error={errors[field?.name]?.message ? 'Required' : ''}
                                isRequiredMsg={'Required'}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {isCML && (
                  <>
                    <div className={style.addFormSection}>
                      <SectionContainer
                        text={'CML'}
                        subSection
                        headerClassName={`${style.utSectionOfPhotos}`}
                      ></SectionContainer>
                    </div>

                    {/* Form */}
                    <div className={style.addDocumentForm}>
                      {formFieldsOfCML?.map((field, index) => {
                        return (
                          <div
                            className={index === formFieldsOfCML?.length - 1 ? '' : style['mb-16']}
                            key={field?.order}
                          >
                            {field?.type === 'Text' && (
                              <div className="op-input-field-wrap">
                                <InputField
                                  reconciled={field?.isMasterData}
                                  showReconciled={isInputReconcile({
                                    fieldName: field?.name,
                                    isMasterData: field?.isMasterData
                                  })}
                                  showErrorMessage={
                                    field?.name === 'cmlNumber' && isCMLNumberALreadyExistsInTheList
                                      ? true
                                      : !field?.isMasterData
                                  }
                                  label={field?.label}
                                  dot={field?.required}
                                  type={'text'}
                                  inputClass="input-field input-regular"
                                  labelColor={style.labelColor}
                                  className={field?.isReadOnly ? style.disabledColor : ''}
                                  register={register}
                                  name={field?.name}
                                  error={
                                    field?.name === 'cmlNumber' && isCMLNumberALreadyExistsInTheList
                                      ? 'Must be unique'
                                      : errors[field?.name]?.message
                                      ? 'Required'
                                      : ''
                                  }
                                  disabled={field?.isReadOnly}
                                />
                              </div>
                            )}
                            {field?.type == 'Dropdown' && (
                              <Select
                                showErrorMessage={!field?.isMasterData}
                                dot={field?.required}
                                label={field?.label}
                                options={field?.options}
                                labelColor={style.labelColor}
                                control={control}
                                name={field?.name}
                                error={errors[field?.name]?.message ? 'Required' : ''}
                                register={register}
                                isClearable
                                disabled={field?.isReadOnly}
                                reconciled={field?.isMasterData}
                                showReconciled={isSelectBoxReconcile({
                                  fieldName: field?.name,
                                  isMasterData: field?.isMasterData,
                                  options: field?.options
                                })}
                                customFunc={
                                  field.name === 'component'
                                    ? (e) => {
                                        const value = e?.value;
                                        changeCOmponentValueEvent({ componentValue: value });
                                      }
                                    : () => {}
                                }
                              />
                            )}

                            {field?.type === 'Readings' && (
                              <>
                                {showTminBox && field?.label === 'Tmin' ? (
                                  <>
                                    <div className={style.textBoxUnit}>
                                      <Label
                                        label={`${field?.label}`}
                                        inLineStyle={{ paddingLeft: '8px' }}
                                        reconciled={false}
                                      />
                                    </div>
                                    <div
                                      className={style.previousReadingContainer}
                                      style={{ marginBottom: '0px', marginTop: '2px' }}
                                    >
                                      <label>{picklistsMap[watch('component')]?.value} TMIN</label>
                                      <p className={!tminBoxValue && style.TminBoxNoValue}>
                                        {tminBoxValue ? tminBoxValue : 'No value available'}
                                      </p>
                                    </div>

                                    <div className={style.infoTMin}>
                                      For head and shell components, the Tmin value is based on
                                      equipment record data and is read-only on this form.
                                    </div>
                                  </>
                                ) : (
                                  <TextBoxUnit
                                    reconciled={field?.isMasterData}
                                    inputLabel={`${field?.label}`}
                                    control={control}
                                    register={register}
                                    inputFiledRequired={field?.required}
                                    setValue={setValue}
                                    watch={watch}
                                    error={
                                      watch(field?.name)?.textBoxValue &&
                                      watch(field?.name)?.textBoxUnitValue
                                        ? null
                                        : errors[field?.name]?.textBoxValue?.message ||
                                          errors[field?.name]?.textBoxUnitValue?.message
                                        ? 'Required'
                                        : null
                                    }
                                    textBoxUnitValueError={
                                      watch(field?.name)?.textBoxUnitValue
                                        ? null
                                        : errors[field?.name]?.textBoxUnitValue?.message
                                        ? true
                                        : null
                                    }
                                    textBoxValueError={
                                      watch(field?.name)?.textBoxValue
                                        ? null
                                        : errors[field?.name]?.textBoxValue?.message
                                        ? true
                                        : null
                                    }
                                    name={field?.name}
                                    options={field?.options}
                                    onKeyDown={(e) =>
                                      onKeyDown({
                                        e,
                                        isTmin: field?.label === 'Tmin'
                                      })
                                    }
                                    showReconciled={isTextBoxUnitReconcile({
                                      fieldName: field?.name,
                                      isMasterData: field?.isMasterData
                                    })}
                                    disabledFieldAndHideActionBtn={true}
                                    onInputBlur={() =>
                                      changeDecimalValueBasedOnUnit({
                                        name: field?.name
                                      })
                                    }
                                    // disabled={showTminBox && field?.label === 'Tmin'}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                      {isNewForm && !isReadingAvailable && (
                        <div className={`${style.addReading}`}>
                          <p className={style.addPSVs} onClick={clickAddNewCMLEvent}>
                            <Icon name={'add-icon'} style={{ marginRight: '4px' }} />
                            Add readings
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {isReadingAvailable && openAddReadingForm && (
                  <>
                    <div className={style.addFormSection}>
                      <SectionContainer
                        text={'Readings'}
                        subSection
                        headerClassName={`${style.utSectionOfPhotos}`}
                      ></SectionContainer>
                    </div>

                    {/* Form */}
                    <div className={style.addDocumentForm}>
                      {formFieldsOfReading?.map((field, index) => {
                        return (
                          <div
                            className={
                              index === formFieldsOfReading?.length - 1 ? '' : style['mb-16']
                            }
                            key={field?.order}
                          >
                            {field?.type === 'Readings' && (
                              <TextBoxUnit
                                inputLabel={`${field?.label}`}
                                control={control}
                                register={register}
                                inputFiledRequired={field?.required}
                                setValue={setValue}
                                watch={watch}
                                error={
                                  watch(field?.name)?.textBoxValue &&
                                  watch(field?.name)?.textBoxUnitValue
                                    ? null
                                    : errors[field?.name]?.textBoxValue?.message ||
                                      errors[field?.name]?.textBoxUnitValue?.message
                                    ? 'Required'
                                    : null
                                }
                                textBoxUnitValueError={
                                  watch(field?.name)?.textBoxUnitValue
                                    ? null
                                    : errors[field?.name]?.textBoxUnitValue?.message
                                    ? true
                                    : null
                                }
                                textBoxValueError={
                                  watch(field?.name)?.textBoxValue
                                    ? null
                                    : errors[field?.name]?.textBoxValue?.message
                                    ? true
                                    : null
                                }
                                name={field?.name}
                                options={field?.options}
                                onKeyDown={(e) => onKeyDown({ e, decimalPlaces: 6 })}
                                disabledFieldAndHideActionBtn={true}
                                onInputBlur={() =>
                                  changeDecimalValueBasedOnUnit({
                                    name: field?.name
                                  })
                                }
                              />
                            )}

                            {field?.type === 'Box' &&
                              (watch(field?.name) || watch(field?.name2)) && (
                                <div className={style.previousReadingContainer}>
                                  <label>{field.label}</label>
                                  <p>
                                    {selectedUnit === 'mm'
                                      ? field?.name2
                                        ? watch(field?.name2)
                                        : field?.name
                                      : watch(field?.name)}
                                  </p>
                                </div>
                              )}

                            {field?.type == 'LongText' && (
                              <Textarea
                                dot={field?.required}
                                label={field?.label}
                                inputClass="input-field input-regular"
                                labelColor={style.labelColor}
                                register={register}
                                name={field?.name}
                                error={errors[field?.name]?.message ? 'Required' : ''}
                                isRequiredMsg={'Required'}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                <div className={style.marginBottom300}></div>
              </div>

              <div className={style.footerBtn}>
                {(cachedData || isReadingAvailable) && (
                  <>
                    {!cachedData?.isCached && isReadingAvailable && (
                      <Icon
                        name={'delete'}
                        alt={'delete'}
                        onClick={deleteEvent}
                        style={{ marginRight: cachedData ? '48px' : '0px' }}
                      />
                    )}

                    <Button
                      btnClass={`btn-fill button-large ${!disabledSkipButton && style.skipBtn}`}
                      type="button"
                      text="Skip"
                      onClick={async () => {
                        const res = await trigger(Object.keys(addEditCMLSchema));
                        if (res && !isCMLNumberALreadyExistsInTheList) {
                          if (!cachedData) {
                            await addNewCMLDataEvent({
                              data: {
                                ...getValues(),
                                selectedLengthUnit: selectedUnit,
                                id: uuidv4(),
                                avgReading: { textBoxValue: null, textBoxUnitValue: '' },
                                minReading: { textBoxValue: null, textBoxUnitValue: '' }
                              },
                              isDone: false,
                              isSkipped: true,
                              isAddReading: false
                            });
                          } else {
                            await updateCMLDataEvent({
                              data: {
                                ...getValues(),
                                selectedLengthUnit: selectedUnit,
                                avgReading: { textBoxValue: null, textBoxUnitValue: '' },
                                minReading: { textBoxValue: null, textBoxUnitValue: '' }
                              },
                              isSkipped: true
                            });
                          }

                          resetAllData();
                        }
                      }}
                      disabled={disabledSkipButton}
                    />
                  </>
                )}

                <Button
                  btnClass="btn-fill button-large"
                  type="submit"
                  form="addCML"
                  text="Save"
                  disabled={isSaveButtonDisabled || disabledBtn}
                />
              </div>
            </>
          )}
        </div>
      </form>

      {isDeletedModalOpen && (
        <DeleteModal
          open={isDeletedModalOpen}
          setOpen={setIsDeletedModalOpen}
          confirmDeletionCML={true}
          recordType={'CML'}
          handleDelete={() => {
            deleteSelectedCML({ id: cachedData?.id });
          }}
        />
      )}

      {saveChanges && (
        <SaveModal
          open={saveChanges}
          setOpen={setSaveChanges}
          saveChanges={true}
          onDiscard={() => {
            resetAllData();
          }}
          onSave={handleSave}
        />
      )}
    </Portal>
  );
};

export default AddEditCMLModal;
